import {
  ActionConfirmationModal,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormSubmitButton,
  Input,
  LabelText,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMassTortCustomField,
  useDeleteMassTortCustomField,
  useUpdateMassTortCustomFields,
} from "@/lib/data/react-query/mass-torts/mass-tort";
import { MASS_TORT_CUSTOM_FIELD_VALUE_TYPE_OPTIONS } from "@/lib/data/schemas/massTorts/MassTortSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MassTortCustomField } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

const VALUE_TYPE_OPTIONS = MASS_TORT_CUSTOM_FIELD_VALUE_TYPE_OPTIONS.map(
  (value) => ({
    value,
    label: value === "select" ? "Select" : value === "date" ? "Date" : "Text",
  }),
);

const formSchema = z
  .object({
    massTortCustomFieldDescription: z
      .string()
      .trim()
      .max(5000, { message: "Description must be 5,000 characters or less" })
      .optional(),
    massTortCustomFieldLabel: z
      .string()
      .trim()
      .min(1, { message: "Label is required" })
      .max(100, { message: "Label must be 100 characters or less" }),
    massTortCustomFieldValueOptions: z.array(
      z.object({
        value: z
          .string()
          .trim()
          .max(100, { message: "Option must be 100 characters or less" }),
      }),
    ),
    massTortCustomFieldValueType: z.enum(
      MASS_TORT_CUSTOM_FIELD_VALUE_TYPE_OPTIONS,
      {
        invalid_type_error: "Value type is required",
        required_error: "Value type is required",
      },
    ),
  })
  .superRefine((data, ctx) => {
    if (data.massTortCustomFieldValueType !== "select") {
      return;
    }

    const options = data.massTortCustomFieldValueOptions;
    if (options.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one option is required",
        path: ["massTortCustomFieldValueOptions"],
      });
      return;
    }

    options.forEach((option, index) => {
      if (!option.value.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Option is required",
          path: ["massTortCustomFieldValueOptions", index, "value"],
        });
      }
    });
  });

type MassTortCustomFieldFormValues = z.infer<typeof formSchema>;
type MassTortCustomFieldPayload = Pick<
  MassTortCustomField,
  "description" | "label" | "valueOptions" | "valueType"
>;

const defaultFormValues: MassTortCustomFieldFormValues = {
  massTortCustomFieldDescription: "",
  massTortCustomFieldLabel: "",
  massTortCustomFieldValueOptions: [{ value: "" }],
  massTortCustomFieldValueType: "select",
};

const FORM_ID = "mass-tort-custom-field-form";

type AddOrEditMassTortCustomFieldModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  massTortId: string;
  massTortCustomFields: MassTortCustomField[];
} & (
  | {
      editing: true;
      activeCustomField: MassTortCustomField;
      activeCustomFieldIndex: number;
      isReadOnly?: boolean;
    }
  | {
      editing: false;
      activeCustomField?: never;
      activeCustomFieldIndex?: never;
      isReadOnly?: never;
    }
);

const getFormValues = (
  customField?: MassTortCustomField,
): MassTortCustomFieldFormValues => ({
  ...defaultFormValues,
  massTortCustomFieldDescription: customField?.description ?? "",
  massTortCustomFieldLabel: customField?.label ?? "",
  massTortCustomFieldValueOptions: customField?.valueOptions?.length
    ? customField.valueOptions.map((value) => ({ value }))
    : [{ value: "" }],
  massTortCustomFieldValueType: customField?.valueType ?? "select",
});

const getPayload = (
  data: MassTortCustomFieldFormValues,
): MassTortCustomFieldPayload => ({
  description: data.massTortCustomFieldDescription?.trim() || null,
  label: data.massTortCustomFieldLabel.trim(),
  valueOptions:
    data.massTortCustomFieldValueType === "select"
      ? data.massTortCustomFieldValueOptions.map((option) =>
          option.value.trim(),
        )
      : null,
  valueType: data.massTortCustomFieldValueType,
});

export function AddOrEditMassTortCustomFieldModal({
  open,
  setOpen,
  massTortId,
  massTortCustomFields,
  editing,
  activeCustomField,
  activeCustomFieldIndex,
  isReadOnly,
}: AddOrEditMassTortCustomFieldModalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useForm<MassTortCustomFieldFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: editing
      ? getFormValues(activeCustomField)
      : defaultFormValues,
    mode: "onSubmit",
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "massTortCustomFieldValueOptions",
  });
  const selectedValueType = form.watch("massTortCustomFieldValueType");

  const createCustomField = useCreateMassTortCustomField();
  const updateCustomFields = useUpdateMassTortCustomFields();
  const deleteCustomField = useDeleteMassTortCustomField();
  const isFormLoading =
    createCustomField.isPending ||
    updateCustomFields.isPending ||
    deleteCustomField.isPending;

  const onSubmit = async (data: MassTortCustomFieldFormValues) => {
    const normalizedLabel = toLower(data.massTortCustomFieldLabel.trim());
    if (
      massTortCustomFields.some(
        (customField, index) =>
          index !== activeCustomFieldIndex &&
          toLower(customField.label.trim()) === normalizedLabel,
      )
    ) {
      form.setError("massTortCustomFieldLabel", {
        message: "Custom field label must be unique",
      });
      return;
    }

    if (editing) {
      const payload = getPayload(data);
      await updateCustomFields.mutateAsync({
        massTortId,
        customFields: [
          {
            ...payload,
            dateModified: activeCustomField.dateModified,
            id: activeCustomField.id,
            order: activeCustomField.order,
          },
        ],
      });
    } else {
      const payload = getPayload(data);
      await createCustomField.mutateAsync({
        massTortId,
        ...payload,
      });
    }
    form.reset();
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!editing) {
      return;
    }
    await deleteCustomField.mutateAsync({
      massTortId,
      customFieldId: activeCustomField.id,
    });
    setIsDeleting(false);
    setOpen(false);
  };

  const title = useMemo(() => {
    if (isReadOnly) return "View custom field";
    return editing ? "Update custom field" : "Create custom field";
  }, [editing, isReadOnly]);

  const description = useMemo(() => {
    if (isReadOnly) return "View the mass tort custom field details below.";
    return editing
      ? "Update the mass tort custom field details below."
      : "Create a custom field for this mass tort.";
  }, [editing, isReadOnly]);

  useEffect(() => {
    if (open) {
      form.reset(
        editing ? getFormValues(activeCustomField) : defaultFormValues,
      );
    }
  }, [activeCustomField, editing, form, open]);

  const canDelete =
    !isReadOnly &&
    editing &&
    Boolean(activeCustomField.id) &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.massTortCustomField,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && <FormSubmitButton submitFormId={FORM_ID} />}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  label="Label*"
                  register={form.register("massTortCustomFieldLabel")}
                />
                <div className="col-span-2">
                  <SelectFormField
                    label="Value type*"
                    register={form.register("massTortCustomFieldValueType")}
                    items={VALUE_TYPE_OPTIONS}
                    itemsKey="value"
                    formatItemLabel={(item) => item?.label}
                    placeholder="Select value type"
                  />
                </div>
                <div className="col-span-2">
                  <TextAreaFormField
                    label="Description"
                    register={form.register("massTortCustomFieldDescription")}
                  />
                </div>
                {selectedValueType === "select" ? (
                  <div className="col-span-2 space-y-2">
                    <div>
                      <Typography variant="subHeading" size="sm">
                        <LabelText>Value options*</LabelText>
                      </Typography>
                    </div>
                    <div className="space-y-2">
                      {fields.map((field, index) => (
                        <div key={field.id} className="flex items-start gap-2">
                          <FormField
                            control={form.control}
                            name={`massTortCustomFieldValueOptions.${index}.value`}
                            render={({ field: optionField }) => (
                              <FormItem className="flex-1">
                                <FormControl>
                                  <Input
                                    {...optionField}
                                    placeholder={`Option ${index + 1}`}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            onClick={() => {
                              remove(index);
                            }}
                            disabled={fields.length <= 1 || isReadOnly}
                            aria-label="Remove option"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    {!isReadOnly ? (
                      <Button
                        type="button"
                        variant="link"
                        className="text-muted-foreground h-auto min-h-0 justify-start p-0"
                        onClick={() => {
                          append({ value: "" });
                        }}
                      >
                        <Plus className="h-4 w-4 shrink-0" />
                        Add option
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title="Are you sure you want to delete this custom field?"
        open={isDeleting && editing}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
}
